/*globals google_api_key :readonly, countryIso :readonly, skyapi :readonly, siteLanguage :readonly */

import {setOptions, importLibrary} from "@googlemaps/js-api-loader"
import {ConvertExperienceService} from '../services/convert-experience-service';
import {CookieService} from '../services/cookie.service';
import {B2bGATracker, FormInteractionType, FormName} from './b2b-ga-tracker-service';
import {UsercentricsService} from './usercentrics-service';

type SelectedPlace = {
    label: string,
    lat: number,
    long: number,
} | null;

type formError = {
    message?: string,
    field: string,
    type: 'required' | 'invalid' | 'duplicate',
};

export class HomeProspectForm {
    private selectedPlace: SelectedPlace = null;
    private form: HTMLFormElement;
    private locationInput: HTMLInputElement;
    private duplicateCheckUrl: URL;
    private emailCheckUrl: URL;
    private activeSubmission: object | null = null;


    async activate(): Promise<void> {
        this.form = document.getElementById('home-prospect-form') as HTMLFormElement;
        this.emailCheckUrl = new URL(this.form.getAttribute('data-email-validate-check-url'), window.location.origin);
        this.duplicateCheckUrl = new URL(this.form.getAttribute('data-email-duplicate-check-url'), window.location.origin);
        this.trackFirstInteraction();
        this.pagehideListener();

        setOptions({
            key: google_api_key,
            language: siteLanguage
        });

        if (!await this.loadPlacesLibrary()) {
            return;
        }

        this.locationInput = this.form.querySelector("input[name='location_input']") as HTMLInputElement;
        const autocompleteOptions = {
            fields: ["formatted_address", "geometry", "name"],
            strictBounds: false,
            componentRestrictions: {country: countryIso},
        };
        const autocomplete = new google.maps.places.Autocomplete(this.locationInput, autocompleteOptions);
        let place = null;

        autocomplete.addListener("place_changed", () => {
            place = autocomplete.getPlace();

            if (!place.geometry || !place.geometry.location) {
                this.selectedPlace = null;
                return;
            }
            this.selectedPlace = {
                label: place.formatted_address || place.name || "",
                lat: place.geometry.location.lat(),
                long: place.geometry.location.lng(),
            }
        });
    }

    private async loadPlacesLibrary(): Promise<boolean> {
        try {
            await this.importPlacesLibrary();
            return true;
        } catch (error) {
            await new Promise<void>(resolve => {
                setTimeout(resolve, 2000);
            });
            try {
                await this.importPlacesLibrary();
                return true;
            } catch (retryError) {
                console.warn("Google Maps loader failed after retry, location autocomplete disabled:", retryError);
                this.showLocationLookupUnavailableNotice();
                return false;
            }
        }
    }

    private async importPlacesLibrary(): Promise<void> {
        const placesLibrary = await importLibrary("places");
        if (!placesLibrary?.Autocomplete) {
            throw new Error("Google Maps places library loaded without Autocomplete");
        }
    }

    private showLocationLookupUnavailableNotice(): void {
        this.form.querySelector(".location-lookup-notice")?.classList.remove("d-none");
    }

    private trackFirstInteraction(): void {
        this.form.addEventListener('focusin', () => {
            B2bGATracker.sendProspectFormInteractionEvent(FormInteractionType.VIEW);
        }, {once: true});
    }

    async submitForm(): Promise<void> {
        const submission = {};
        this.activeSubmission = submission;
        this.setSubmitting(true);
        try {
            await this.checkForm();
        } catch (error) {
            this.setSubmitting(false);
            if (Array.isArray(error)) {
                B2bGATracker.sendProspectFormInteractionEvent(FormInteractionType.ERROR, {
                    errorData: (error as formError[]).map((e) => ({errorField: e.field, errorType: e.type})),
                });
            }
            return;
        }
        if (this.activeSubmission !== submission) {
            return;
        }

        let url = `${skyapi as string}/companies/create-prospect-minimal`;
        try {
            let formData = {
                primaryCategory: parseInt((this.form.querySelector("*[name='category']") as HTMLSelectElement | HTMLInputElement).value),
                contactEmail: (this.form.querySelector("input[name='email']") as HTMLInputElement).value,
                promoCode: (this.form.querySelector("input[name='promoCode']") as HTMLInputElement).value,
                authcode: (this.form.querySelector("input[name='authcode']") as HTMLInputElement).value,
                location: this.selectedPlace,
                abTest: document.body.dataset.ab,
                // getAllCookies rather than getCookie: only the former percent-decodes the value.
                experiments: CookieService.getAllCookies()['sd_exp'] || null,
                convertExperiences: [],
                cookieConsent: this.getCookieConsentData(),
            }

            try {
                formData.convertExperiences = ConvertExperienceService.getAllExperiences().map(
                    (exp) => `${exp.experimentId}:${exp.variationId}`
                );
            } catch (error) {
                console.error("Failed to get convert experiences:", error);
            }

            formData = {...formData, ...this.getTrackingCookies()};

            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (this.activeSubmission !== submission) {
                return;
            }

            if (!response.ok) {
                console.error("Failed to submit form:", response.statusText);
                const emailError = await this.emailErrorFromResponse(response);
                if (emailError) {
                    this.applyErrors([emailError]);
                }
                this.setSubmitting(false);
                return;
            }

            const data = await response.json();

            B2bGATracker.sendProspectFormInteractionEvent(FormInteractionType.SUBMIT, {
                businessCategoryId: formData.primaryCategory,
            });
            await B2bGATracker.sendFormStartEvent(FormName.BusinessRegistration);

            if (this.activeSubmission !== submission) {
                return;
            }

            if (data.redirect_url) {
                // Keep the button in its loading state — the page is navigating away.
                await this.redirectTo(data.redirect_url as string);
            } else {
                console.warn("No redirect URL found in response");
                this.setSubmitting(false);
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            this.setSubmitting(false);
        }

    }

    private pagehideListener(): void {
        // Reset before the page enters the bfcache, otherwise browser 'back' restores the button stuck in loading state
        window.addEventListener("pagehide", () => {
            this.activeSubmission = null;
            this.setSubmitting(false);
        });
    }

    private setSubmitting(state: boolean): void {
        const btn = document.getElementById("start-prospect-sign-up-flow") as HTMLButtonElement;
        if (!btn) {
            return;
        }
        const spinner = btn.querySelector(".spinner-border") as HTMLElement;
        if (!spinner) {
            return;
        }

        if (state) {
            btn.disabled = true;
            btn.classList.remove("btn-arrow");
            btn.classList.add("loading");
            spinner.classList.remove("d-none");
        } else {
            btn.disabled = false;
            btn.classList.add("btn-arrow");
            btn.classList.remove("loading");
            spinner.classList.add("d-none");
        }
    }

    private async redirectTo(url: string): Promise<void> {
        window.location.assign(await window.skyComponents.crossDomainLinkService.getCrossDomainUrl(url) as string);
    }

    private async checkForm(): Promise<void> {
        const errors: formError[] = [];
        this.form.querySelectorAll(".error-message").forEach(el => el.classList.add("d-none"));
        this.form.querySelectorAll(".error").forEach(el => el.classList.remove("error"));

        const categorySelect = this.form.querySelector<HTMLSelectElement>("select[name='category']");
        if (categorySelect && (!categorySelect.value || categorySelect.value === "")) {
            errors.push({ type: 'required' , field: 'category' });
        }

        if (this.selectedPlace === null) {
            errors.push({ type: 'required', field: 'location_input' });
        }

        const emailInput = this.form.querySelector<HTMLInputElement>("input[name='email']");
        const email = emailInput?.value.trim() ?? "";
        if (!emailInput || email === "") {
            errors.push({ type: 'required', field: 'email' });
        } else if (!emailInput.checkValidity()) {
            errors.push({ type: 'invalid', field: 'email' });
        } else {
            try {
                await Promise.all([
                    this.checkEmailRemote(email).catch(() => {
                        throw { type: 'invalid' as const, field: 'email' };
                    }),
                    this.duplicateEmailCheck(email).catch(() => {
                        throw { type: 'duplicate' as const, field: 'email' };
                    }),
                ]);
            } catch (err) {
                errors.push(err as formError);
            }
        }

        if (errors.length > 0) {
            this.applyErrors(errors);
            throw errors;
        }
    }

    // Only a violation on the email field may mark the email field: a rate limit or a server error is not the address' fault.
    private async emailErrorFromResponse(response: Response): Promise<formError | null> {
        if (response.status === 409) {
            return { type: 'duplicate', field: 'email' };
        }

        let violations: unknown;
        try {
            violations = (await response.json()).violations;
        } catch {
            return null;
        }

        if (!Array.isArray(violations)) {
            return null;
        }

        const emailViolation = violations.find(violation => violation?.propertyPath === 'contactEmail');
        if (!emailViolation) {
            return null;
        }

        const isDuplicate = typeof emailViolation.message === 'string' && emailViolation.message.includes('is not unique');

        return { type: isDuplicate ? 'duplicate' : 'invalid', field: 'email' };
    }

    private applyErrors(errors: formError[]): void {
        errors.forEach(err => {
            const field = this.form.querySelector<HTMLElement>(`[name='${err.field}']`);
            if (!field) return;

            field.classList.add("error");

            const wrapper = field.closest(".form-field-wrapper");
            const errorContainer = wrapper?.querySelector<HTMLParagraphElement>(
                `.error-message[data-error='${err.type}']`
            );

            if (errorContainer) {
                errorContainer.classList.remove("d-none");
            }
        });
    }


    private async checkEmailRemote(email: string): Promise<void> {
        if (!email) {
            return Promise.reject();
        }

        const url = new URL(this.emailCheckUrl);
        url.searchParams.set('value', email);

        const delays = [100, 100];

        for (let attempt = 0; attempt <= delays.length; attempt++) {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Email check failed with status ${response.status}`);
                }
                const valid = await response.json() as boolean;
                if (!valid) {
                    return Promise.reject();
                }
                return Promise.resolve();
            } catch (error) {
                if (attempt === delays.length) {
                    // Quiet no-op on network failure: the server-side check still guards submission
                    console.warn('Email check failed after retries, skipping:', error);
                    return Promise.resolve();
                }
                await new Promise(resolve => setTimeout(resolve, delays[attempt]));
            }
        }
    }

    private async duplicateEmailCheck(email: string): Promise<void> {
        if (!email) {
            return;
        }

        const url = new URL(this.duplicateCheckUrl);
        url.searchParams.set('value', email);

        const delays = [100, 100];

        for (let attempt = 0; attempt <= delays.length; attempt++) {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Duplicate email check failed with status ${response.status}`);
                }
                const valid = await response.json() as boolean;
                if (!valid) {
                    return Promise.reject('Email is already in use');
                }
                return Promise.resolve();
            } catch (error) {
                if (attempt === delays.length) {
                    // Quiet no-op on network failure: the server-side check still guards submission
                    console.warn('Duplicate email check failed after retries, skipping:', error);
                    return Promise.resolve();
                }
                await new Promise(resolve => setTimeout(resolve, delays[attempt]));
            }
        }
    }

    private getTrackingCookies(): Record<string, string> {
        const cookieMap: Record<string, string> = {
            'b2b_utm_converted': 'b2bUtmConverted',
            'b2b_utm_original': 'b2bUtmOriginal',
            'b2c_utm_original': 'b2cUtmOriginal',
            'gclid': 'gclid',
            'gbraid': 'gbraid',
            'wbraid': 'wbraid',
            'msclkid': 'msclkid',
            '_fbc': '_fbc',
            '_fbp': '_fbp',
        };

        const allCookies = CookieService.getAllCookies();
        const mappedCookies: Record<string, string> = {};

        Object.entries(cookieMap).forEach(([cookieName, targetKey]) => {
            const cookieValue = allCookies[cookieName];
            if (cookieValue) {
                mappedCookies[targetKey] = cookieValue;
            }
        });

        return mappedCookies;
    }

    private getCookieConsentData(): Record<string, boolean> {
        if (!window.skyComponents ||
            !window.skyComponents.usercentricsService ||
            !(window.skyComponents.usercentricsService instanceof UsercentricsService)
        ) {
            return {};
        }
        const usercentricsService: UsercentricsService = window.skyComponents.usercentricsService;

        if (!usercentricsService.isInitialized()) {
            console.warn('"usercentricsService" is not initialized.');
            return {};
        }

        return {
            'googleAds': usercentricsService.getCookieConsentData('Google Ads'),
            'facebookPixel': usercentricsService.getCookieConsentData('Facebook Pixel')
        };
    }
}

(<any>window).skyComponents = (<any>window).skyComponents || {};
(<any>window).skyComponents.homeProspectForm = new HomeProspectForm();
(<any>window).skyComponents.homeProspectForm.activate().catch((error: unknown) => {
    console.error("Failed to activate home prospect form:", error);
});
